import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { Mark, Wordmark, LogoImage } from "@/components/Brand";
import { ArrowRight } from "lucide-react";

const inputCls =
  "w-full rounded-md border bg-brand-panel px-3.5 py-2.5 text-sm text-brand-ink outline-none transition-all duration-150 focus:border-brand-accent focus:ring-2 focus:ring-brand-accent/20";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setFormError("");
    const errs = {};
    if (password.length < 8) errs.password = "Al menos 8 caracteres.";
    if (confirm !== password) errs.confirm = "Las contraseñas no coinciden.";
    setErrors(errs);
    if (Object.keys(errs).length || submitting) return;
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Contraseña cambiada. Ya puedes entrar.");
      navigate("/acceso");
    } catch (err) {
      setFormError(apiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-brand-canvas px-6 py-12">
      <div className="w-full max-w-sm">
        <Link to="/" className="mb-8 block">
          <LogoImage size="lg" />
        </Link>
        <h1 className="text-3xl font-extrabold tracking-tight text-brand-ink" style={{ letterSpacing: "-0.02em" }}>Nueva contraseña</h1>
        <p className="mt-2 text-sm text-brand-muted">Elige una contraseña nueva para tu cuenta.</p>

        {!token ? (
          <div className="mt-8 rounded-md border border-brand-neg/30 bg-brand-neg/[0.06] p-4 text-sm text-brand-neg" data-testid="reset-notoken">
            Falta el enlace de recuperación. Pídelo de nuevo desde <Link to="/recuperar" className="font-semibold underline">aquí</Link>.
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-5" noValidate>
            <div>
              <label htmlFor="password" className="mb-1.5 block text-sm font-semibold text-brand-ink">Nueva contraseña</label>
              <input id="password" type="password" data-testid="reset-password-input" value={password} onChange={(e) => { setPassword(e.target.value); setErrors((x) => ({ ...x, password: "" })); }} placeholder="Mínimo 8 caracteres" className={`${inputCls} ${errors.password ? "border-brand-neg" : "border-brand-line"}`} />
              {errors.password && <p className="mt-1.5 text-xs font-medium text-brand-neg" data-testid="reset-password-error">{errors.password}</p>}
            </div>
            <div>
              <label htmlFor="confirm" className="mb-1.5 block text-sm font-semibold text-brand-ink">Repite la contraseña</label>
              <input id="confirm" type="password" data-testid="reset-confirm-input" value={confirm} onChange={(e) => { setConfirm(e.target.value); setErrors((x) => ({ ...x, confirm: "" })); }} placeholder="La misma otra vez" className={`${inputCls} ${errors.confirm ? "border-brand-neg" : "border-brand-line"}`} />
              {errors.confirm && <p className="mt-1.5 text-xs font-medium text-brand-neg" data-testid="reset-confirm-error">{errors.confirm}</p>}
            </div>
            {formError && <div className="rounded-md border border-brand-neg/30 bg-brand-neg/[0.06] px-3.5 py-2.5 text-sm font-medium text-brand-neg" data-testid="reset-form-error">{formError}</div>}
            <button type="submit" disabled={submitting} data-testid="reset-submit-button" className="flex w-full items-center justify-center gap-2 rounded-md bg-brand-accent px-4 py-2.5 text-sm font-bold text-brand-canvas transition-all duration-150 hover:brightness-110 disabled:opacity-60">
              {submitting ? "Guardando…" : "Guardar contraseña"} {!submitting && <ArrowRight size={16} />}
            </button>
          </form>
        )}

        <p className="mt-8 text-center text-sm text-brand-muted">
          <Link to="/acceso" className="font-semibold text-brand-accent hover:underline">← Volver al acceso</Link>
        </p>
      </div>
    </div>
  );
}
